// RelationshipEditForm — correct a recorded relationship in place (SEP-214).
//
// WHY THIS EXISTS. Ending a relationship was the only way to retract a
// mistake, and an ended link is permanent: it stays on the register, greyed,
// with its end date, saying two endpoints were once connected when they never
// were. The firm asked to edit the row instead. Ending keeps its own meaning —
// the truthful close of a link that really did exist.
//
// ONE FORM, TWO SURFACES. The per-record panel and the book-level register
// both mount it, so a correction cannot mean two different things depending on
// where a reader spotted the mistake. Each surface hands over the row in
// CANONICAL stored order, because that is the order `update-relationship`
// takes; the panel converts its own perspective on the way in.
//
// THE PARTIES ARE READ-ONLY TEXT, and that is the point of the component
// rather than an omission from it. A different pairing is a different
// relationship: the ownership unique index and the same-owner rule are both
// built on the stored pair. To re-point an end, end this link and record the
// real one.
//
// IT CORRECTS THE ROLES AND THE NOTE, and no longer the start date (SEP-227):
// the date left the create form and the per-record table on a product decision,
// and a field a reader can correct but never enter is a field nobody fills.
// `startDate` stays a PATCH field on the wire, and the form simply omits it, so
// a stored date survives every correction made here.
//
// It renders INLINE, never in a Modal — a scenario cannot assert inside a
// portal that mounts outside <main>, and this form's whole job is to be
// asserted on.

import { useState } from 'react';
import { Button, Select } from '@/components/ui';
import {
    type RelationshipAppliesTo,
    type RelationshipEndpointType,
    type RelationshipRoleDto,
    RELATIONSHIP_ENDPOINT_TYPE_LABELS,
} from '@septima/contract';
import { useUpdateRelationship } from '@/lib/relationships/hooks';

/**
 * How many role slots a reader may actually set.
 *
 * Only `contact_contact` carries two genuinely distinct roles (Parent/Child,
 * Spouse/Spouse). The three vertical kinds carry ONE role each per PRD §9.1 —
 * the contact's role on the record or client, or the record's single `Owner`
 * over an Account — and the other end mirrors that key by storage convention
 * (`vendor/septima-ontology-wealth/packages/ontology-wealth/specs/client/relationship.md`). Offering a second select there would
 * invite a reader to set a value the model has no room for.
 *
 * This is deliberately NOT `isSingleRoleRelationshipKind`, which answers a
 * different question — whether to DISPLAY the mirrored end — and excludes
 * `suitability_account` because neither of its ends is a contact.
 */
function roleSlots(kind: RelationshipAppliesTo): 1 | 2 {
    return kind === 'contact_contact' ? 2 : 1;
}

/** One end, as each surface hands it over — canonical stored order. */
export interface RelationshipEditEndpoint {
    readonly endpointType: RelationshipEndpointType;
    /** Display label, or null when the endpoint sits outside the caller's read. */
    readonly label: string | null;
    readonly roleKey: string;
}

export interface RelationshipEditFormProps {
    relationshipId: string;
    kind: RelationshipAppliesTo;
    /** Canonical endpoint 1 — NOT the surface's own perspective. */
    endpoint1: RelationshipEditEndpoint;
    endpoint2: RelationshipEditEndpoint;
    note: string | null;
    roles: readonly RelationshipRoleDto[];
    onCancel: () => void;
    /** Saved — the surface closes the form and renders any amber warnings. */
    onSaved: (warnings: string[]) => void;
}

const LABEL = 'text-annotation text-[rgb(var(--color-text-tertiary))]';
const FIELD =
    'h-8 px-2 rounded-button border border-[rgb(var(--color-divider))] ' +
    'bg-[rgb(var(--color-surface-primary))] text-body text-[rgb(var(--color-text-primary))]';

/** An endpoint, as the form shows it: the name it already has, and its tier. */
function EndpointText({ endpoint }: { endpoint: RelationshipEditEndpoint }) {
    return (
        <span className="text-body text-[rgb(var(--color-text-primary))]">
            {endpoint.label ?? 'Not in your book'}
            <span className={`ml-2 ${LABEL}`}>
                {RELATIONSHIP_ENDPOINT_TYPE_LABELS[endpoint.endpointType]}
            </span>
        </span>
    );
}

export default function RelationshipEditForm({
    relationshipId,
    kind,
    endpoint1,
    endpoint2,
    note,
    roles,
    onCancel,
    onSaved,
}: RelationshipEditFormProps) {
    const [role1, setRole1] = useState(endpoint1.roleKey);
    const [role2, setRole2] = useState(endpoint2.roleKey);
    // Held as a string so an emptied box is distinguishable from an untouched
    // one: '' means "clear it", which the patch sends as an explicit null.
    const [text, setText] = useState(note ?? '');
    const update = useUpdateRelationship();

    const kindRoles = roles.filter((role) => role.active && role.appliesTo === kind);
    const slots = roleSlots(kind);
    // A single-role kind stores the SAME key on both ends by convention, so one
    // select drives both. Two slots means contact↔contact, where the two roles
    // are genuinely different and a flip may re-canonicalise the row.
    const effectiveRole2 = slots === 1 ? role1 : role2;

    const dirty =
        role1 !== endpoint1.roleKey ||
        effectiveRole2 !== endpoint2.roleKey ||
        text !== (note ?? '');

    const save = () => {
        void update
            .mutateAsync({
                relationshipId,
                patch: {
                    endpoint1Role: role1,
                    endpoint2Role: effectiveRole2,
                    // The start date left the form with the date columns
                    // (SEP-227). `startDate` is a PATCH field, so an absent one
                    // leaves the stored value exactly as it is — a correction
                    // to a role must never clear a date nobody was shown.
                    // '' clears the note; the field is always present, because
                    // a reader who emptied the box meant to empty it.
                    note: text.trim() === '' ? null : text.trim(),
                },
            })
            .then((outcome) => onSaved(outcome.warnings))
            // Failure surfaces via update.error below; catch so the rejection
            // never escapes unhandled.
            .catch(() => undefined);
    };

    return (
        <div className="flex flex-col gap-3 rounded-card border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-secondary))] p-3">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div className="flex items-center gap-2">
                    <span className={LABEL}>Party 1</span>
                    <EndpointText endpoint={endpoint1} />
                </div>
                <div className="flex items-center gap-2">
                    <span className={LABEL}>Party 2</span>
                    <EndpointText endpoint={endpoint2} />
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="flex items-center gap-2">
                    <span className={LABEL}>{slots === 1 ? 'Role' : 'Role 1'}</span>
                    <Select
                        size="sm"
                        value={role1}
                        aria-label={slots === 1 ? 'Role' : 'Role 1'}
                        onChange={(e) => setRole1(e.target.value)}
                    >
                        {kindRoles.map((role) => (
                            <option key={role.key} value={role.key}>
                                {role.label}
                            </option>
                        ))}
                    </Select>
                </div>
                {slots === 2 && (
                    <div className="flex items-center gap-2">
                        <span className={LABEL}>Role 2</span>
                        <Select
                            size="sm"
                            value={role2}
                            aria-label="Role 2"
                            onChange={(e) => setRole2(e.target.value)}
                        >
                            {kindRoles.map((role) => (
                                <option key={role.key} value={role.key}>
                                    {role.label}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </div>

            <div className="flex items-center gap-2">
                <span className={LABEL}>Note</span>
                <input
                    type="text"
                    value={text}
                    aria-label="Note"
                    placeholder="Optional"
                    onChange={(e) => setText(e.target.value)}
                    className={`${FIELD} flex-1 min-w-0`}
                />
            </div>

            <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" size="sm" disabled={update.isPending} onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    busy={update.isPending}
                    disabled={!dirty || update.isPending}
                    onClick={save}
                >
                    Save changes
                </Button>
            </div>

            {update.error instanceof Error && (
                <p className="text-annotation text-[rgb(var(--color-rag-red))]">
                    {update.error.message}
                </p>
            )}
        </div>
    );
}
